import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  Alert,
  Stack,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { loadStripe } from "@stripe/stripe-js";
import { publishableKey, useCreditStore } from "ui";

const stripePromise = loadStripe(publishableKey, {
  developerTools: { assistant: { enabled: false } },
});
// Module-level catch so an early load failure (adblocker, offline mobile,
// ITP) doesn't surface as an unhandled rejection on routes where this
// component isn't mounted (MATTOBOARD-6Q4). The promise still rejects for
// downstream awaits inside the component, where the existing try/catch
// already handles it for user-facing UX.
stripePromise.catch((err) => {
  console.error("[CreditPaymentSuccess] Failed to load Stripe.js:", err);
});

enum PaymentStatus {
  LOADING = "loading",
  SUCCESS = "success",
  FAILED = "failed",
  ERROR = "error",
}

export const CreditPaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<PaymentStatus>(PaymentStatus.LOADING);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const { fetchBalance, fetchTeamBalance, designFirmId, hideTeamBalance } =
    useCreditStore();
  const useTeamBalance = Boolean(designFirmId && !hideTeamBalance);

  // Refs for timeout cleanup
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const processingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const isMountedRef = useRef(true);

  const refreshBalance = useCallback(
    (force = false) => {
      if (useTeamBalance && designFirmId) {
        fetchTeamBalance(designFirmId, force);
        return;
      }
      fetchBalance(force);
    },
    [designFirmId, fetchBalance, fetchTeamBalance, useTeamBalance],
  );

  useEffect(() => {
    isMountedRef.current = true;

    const verifyPayment = async () => {
      try {
        const paymentIntentClientSecret = searchParams.get(
          "payment_intent_client_secret",
        );
        const paymentIntentId = searchParams.get("payment_intent");

        if (!paymentIntentClientSecret || !paymentIntentId) {
          console.error(
            "[CreditPaymentSuccess] Missing payment_intent or client_secret",
          );
          if (!isMountedRef.current) return;
          setErrorMessage("Invalid payment confirmation link");
          setStatus(PaymentStatus.ERROR);
          return;
        }

        // Get Stripe instance
        const stripe = await stripePromise;
        if (!stripe) {
          throw new Error(
            "Unable to load the payment system. Please disable any ad blockers and refresh the page.",
          );
        }

        if (!isMountedRef.current) return;

        // Retrieve payment intent from Stripe
        const { paymentIntent, error: stripeError } =
          await stripe.retrievePaymentIntent(paymentIntentClientSecret);

        if (!isMountedRef.current) return;

        if (stripeError) {
          console.error("[CreditPaymentSuccess] Stripe error:", stripeError);
          setErrorMessage(stripeError.message || "Failed to verify payment");
          setStatus(PaymentStatus.FAILED);
          return;
        }

        if (!paymentIntent) {
          throw new Error("Payment intent not found");
        }

        // Check payment status
        if (paymentIntent.status === "succeeded") {
          // Refresh credit balance (backend webhook should have already added credits)
          await refreshBalance(true);
          if (!isMountedRef.current) return;
          setStatus(PaymentStatus.SUCCESS);

          // Auto-redirect after 3 seconds
          successTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current) {
              navigate("/home/concepts", { replace: true });
            }
          }, 3000);
        } else if (paymentIntent.status === "processing") {
          // Track retry count to prevent infinite refresh loop
          const retryCount = parseInt(searchParams.get("retry") || "0", 10);
          const MAX_RETRIES = 5;

          if (retryCount >= MAX_RETRIES) {
            setErrorMessage(
              "Payment is still processing. Please check back later or contact support if credits don't appear within a few minutes.",
            );
            setStatus(PaymentStatus.ERROR);
            return;
          }

          setErrorMessage(
            "Your payment is being processed. Credits will be added shortly.",
          );
          setStatus(PaymentStatus.LOADING);

          // Check again after a delay
          processingTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current) {
              const url = new URL(window.location.href);
              url.searchParams.set("retry", String(retryCount + 1));
              window.location.href = url.toString();
            }
          }, 3000);
        } else if (paymentIntent.status === "requires_payment_method") {
          setErrorMessage("Payment was not completed. Please try again.");
          setStatus(PaymentStatus.FAILED);
        } else {
          setErrorMessage(
            `Payment ${paymentIntent.status}. Please contact support if credits are missing.`,
          );
          setStatus(PaymentStatus.FAILED);
        }
      } catch (error) {
        console.error("[CreditPaymentSuccess] Verification error:", error);
        if (!isMountedRef.current) return;
        setErrorMessage(
          error instanceof Error ? error.message : "Failed to verify payment",
        );
        setStatus(PaymentStatus.ERROR);
      }
    };

    verifyPayment();

    // Cleanup on unmount
    return () => {
      isMountedRef.current = false;
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
    };
  }, [searchParams, navigate, refreshBalance]);

  const handleReturnHome = () => {
    navigate("/home/concepts", { replace: true });
  };

  if (status === PaymentStatus.LOADING) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#fafafb",
          p: 3,
        }}
      >
        <CircularProgress size={60} sx={{ mb: 3 }} />
        <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
          Verifying Payment...
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Please wait while we confirm your payment
        </Typography>
      </Box>
    );
  }

  if (status === PaymentStatus.SUCCESS) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#fafafb",
          p: 3,
        }}
      >
        <Stack
          spacing={3}
          sx={{ maxWidth: "500px", textAlign: "center", alignItems: "center" }}
        >
          <CheckCircleIcon sx={{ fontSize: "6rem", color: "#10B981" }} />
          <Typography variant="h3" sx={{ fontWeight: "bold" }}>
            Payment Successful!
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Your credits have been added to your account
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Redirecting you to your workspace...
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleReturnHome}
            sx={{
              mt: 2,
              background: "linear-gradient(135deg, #C68CD6 0%, #A869B8 100%)",
              textTransform: "none",
              fontWeight: "bold",
              px: 4,
              "&:hover": {
                background: "linear-gradient(135deg, #B57BC5 0%, #9758A7 100%)",
              },
            }}
          >
            Go to Home
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#fafafb",
        p: 3,
      }}
    >
      <Stack
        spacing={3}
        sx={{ maxWidth: "500px", textAlign: "center", alignItems: "center" }}
      >
        <ErrorOutlineIcon sx={{ fontSize: "6rem", color: "#EF4444" }} />
        <Typography variant="h3" sx={{ fontWeight: "bold" }}>
          {status === PaymentStatus.FAILED
            ? "Payment Failed"
            : "Something Went Wrong"}
        </Typography>
        {errorMessage && (
          <Alert severity="error" sx={{ width: "100%", textAlign: "left" }}>
            {errorMessage}
          </Alert>
        )}
        <Typography variant="body1" color="text.secondary">
          {status === PaymentStatus.FAILED
            ? "Your payment could not be completed. Please try again."
            : "We couldn't verify your payment. If you were charged, please contact support."}
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={handleReturnHome}
          sx={{
            mt: 2,
            background: "linear-gradient(135deg, #C68CD6 0%, #A869B8 100%)",
            textTransform: "none",
            fontWeight: "bold",
            px: 4,
            "&:hover": {
              background: "linear-gradient(135deg, #B57BC5 0%, #9758A7 100%)",
            },
          }}
        >
          Go to Home
        </Button>
      </Stack>
    </Box>
  );
};
